import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Typography, Box, Stack } from '@mui/material';
// components
import Logo from '../components/Logo';
import Image from '../components/Image';
import useAuth from '../hooks/useAuth';
import { APP_NAME } from '../config';
// assets
// unused import removed

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        display: 'flex',
        minHeight: '100vh',
    },
}));

const SectionStyle = styled('div')(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        position: 'fixed', // Keep branding fixed while form scrolls
        top: 0,
        left: 0,
        bottom: 0,
        width: '45%', 
        backgroundColor: '#161C24', 
        color: theme.palette.common.white,
    },
}));

const ContentStyle = styled('div')(({ theme }) => ({
    flexGrow: 1,
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.up('md')]: {
        marginLeft: '45%', // Offset for fixed section
        width: '55%',
        padding: theme.spacing(8, 10),
    },
}));

const LogoStyle = styled(Box)(({ theme }) => ({
    top: theme.spacing(5),
    left: theme.spacing(5),
    zIndex: 9,
    position: 'absolute',
    [theme.breakpoints.down('md')]: {
        position: 'static',
        padding: theme.spacing(3, 3, 0, 3),
        marginBottom: theme.spacing(3),
    },
}));

const BrandingImage = styled('img')({
    width: '80%',
    maxWidth: 450,
    marginBottom: 40,
    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))',
});

// ----------------------------------------------------------------------

AuthLayout.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
};

export default function AuthLayout({ children }) {
    const { setting } = useAuth();
    const appName = setting?.general?.app_name || APP_NAME;

    return (
        <RootStyle>
            <SectionStyle>
                <LogoStyle sx={{ top: 40, left: 40 }}>
                     <Logo />
                </LogoStyle>

                <Box sx={{ px: 5, textAlign: 'center', zIndex: 10 }}>
                     <BrandingImage src="/logo.png" alt="Branding" />
                     <Typography variant="h3" sx={{ mb: 2, fontWeight: 800 }}>
                        Get Started with {appName}
                     </Typography>
                     <Typography sx={{ opacity: 0.7, maxWidth: 380, mx: 'auto', fontSize: '1.1rem' }}>
                        Seamless Payments, Boundless Growth. Experience the next generation of digital finance.
                     </Typography>
                     
                     <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'grey.700' }} />
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'grey.700' }} />
                     </Stack>
                </Box>
                
                {/* Subtle Background Glow */}
                <Box sx={{
                    position: 'absolute',
                    bottom: -100,
                    left: -100,
                    width: 400,
                    height: 400,
                    bgcolor: 'primary.main',
                    filter: 'blur(150px)',
                    opacity: 0.1,
                    zIndex: 1
                }} />
            </SectionStyle>

            <ContentStyle>
                {/* Logo for Mobile Only */}
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 5, textAlign: 'center' }}>
                    <Logo sx={{ mx: 'auto' }} />
                </Box>

                <Stack sx={{ width: 1, maxWidth: 480, margin: 'auto' }}>
                    {children}
                    
                    <Box sx={{ mt: 8, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.8 }}>
                            © {appName} {new Date().getFullYear()}
                        </Typography>
                    </Box>
                </Stack>
            </ContentStyle>
        </RootStyle>
    );
}
